import { Injectable, inject } from '@angular/core';
import { ApiService } from '../../../core/services/api.service';
import { BessContractSupportRow, Contract } from '../../../core/models/domain.models';

export type ContractAnalysisTransactionType = 'sale_purchase' | 'banking' | 'hybrid';

export type ContractAnalysisReportSummary = {
  total_scheduled_mwh: number;
  total_delivered_mwh: number;
  applications_punched: number;
  applications_approved: number;
  active_contracts: number;
  approval_rate: number;
};

export type ContractAnalysisReportRow = {
  contract_id: string;
  contract_number: string;
  buyer_loa_number: string | null;
  seller_loa_number: string | null;
  transaction_type: ContractAnalysisTransactionType;
  buyer_name: string | null;
  seller_name: string | null;
  requested_quantum_mw: number;
  approved_quantum_mw: number;
  days_difference: number;
  time_difference_hours: number;
  total_quantum_mwh: number;
  delivered_mwh: number;
  deviation_percent: number;
  estimated_profit: number | null;
  actual_profit: number | null;
  period_from: string | null;
  period_to: string | null;
};

export type ContractAnalysisReportResponse = {
  summary: ContractAnalysisReportSummary;
  rows: ContractAnalysisReportRow[];
};

@Injectable({ providedIn: 'root' })
export class ContractsService {
  private readonly api = inject(ApiService);

  list() {
    return this.api.get<Contract[]>('/api/contracts');
  }

  listBessSupport() {
    return this.api.get<BessContractSupportRow[]>('/api/bess/contracts-support');
  }

  analysisReport() {
    return this.api.get<ContractAnalysisReportResponse>('/api/contracts/analysis-report');
  }

  get(id: string) {
    return this.api.get<Contract>(`/api/contracts/${id}`);
  }

  create(payload: Partial<Contract>) {
    return this.api.post<Contract>('/api/contracts', payload);
  }

  update(id: string, payload: Partial<Contract>) {
    return this.api.patch<Contract>(`/api/contracts/${id}`, payload);
  }

  accountsApprove(id: string, comments?: string) {
    return this.api.post<Contract>(`/api/contracts/${id}/accounts-approve`, { comments: comments || null });
  }

  accountsReject(id: string, comments?: string) {
    return this.api.post<Contract>(`/api/contracts/${id}/accounts-reject`, { comments: comments || null });
  }

  operationApprove(id: string, comments?: string) {
    return this.api.post<Contract>(`/api/contracts/${id}/operation-approve`, { comments: comments || null });
  }

  operationReject(id: string, comments?: string) {
    return this.api.post<Contract>(`/api/contracts/${id}/operation-reject`, { comments: comments || null });
  }

  delete(id: string) {
    return this.api.delete<{ message: string }>(`/api/contracts/${id}`);
  }
}
